import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../misc/Dialog";
import { Button } from "../../misc/Button";
import CustomToast from "../../misc/CustomToast";
import Spinner from "../../misc/Spinner";
import SlideshowTextEditor, {
    SlideshowSlide,
} from "../../listings/slideshow/SlideshowTextEditor";
import { SlideshowText } from "../../listings/slideshow/types";
import { SlideshowTemplateId } from "../../listings/slideshow/slideshowTemplates";
import { getAuthHeader } from "../../../utils/authHeader";
import { ListingType } from "../../../types/userListing";
import {
    SlideshowEditContext,
    SlideshowRerenderResponse,
} from "../../../types/slideshow";

interface EditSlideshowCaptionsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    postId: string;
    // Called with the new render job id once the re-render has been accepted.
    onRerenderStarted: (jobId: string) => void;
}

// MB-1314: edit the captions on an automatically generated video post and send it back
// for re-rendering. The editor itself is the manual slideshow wizard's SlideshowTextEditor,
// reused verbatim — this dialog only loads the post's existing render and saves the result.
const EditSlideshowCaptionsDialog: React.FC<EditSlideshowCaptionsDialogProps> = ({
    isOpen,
    onClose,
    postId,
    onRerenderStarted,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [isGeneratingText, setIsGeneratingText] = useState<boolean>(false);
    const [context, setContext] = useState<SlideshowEditContext | null>(null);
    const [slides, setSlides] = useState<SlideshowSlide[]>([]);
    const [texts, setTexts] = useState<SlideshowText[]>([]);
    const [aiTexts, setAiTexts] = useState<SlideshowText[]>([]);
    // MB-1314 (O3): set only if the user regenerates captions in the editor. Sent on
    // save so the debug artifact for that run is linked to the re-render job.
    const [captionRenderId, setCaptionRenderId] = useState<string | undefined>();

    const loadContext = useCallback(async (): Promise<void> => {
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.get<SlideshowEditContext>(
                `${backendUrl}/listing/slideshow/post/${postId}`,
                { headers }
            );

            // Seed the slides and their captions in the SAME render. SlideshowTextEditor
            // resets texts whenever texts.length !== slides.length, so setting slides
            // first would blank the captions we just loaded.
            setContext(data);
            setSlides(data.slides);
            setTexts(data.texts);
            setAiTexts(data.aiTexts ?? data.texts);
        } catch (error) {
            const message =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : "Couldn't load this video's captions. Please try again.";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 4000,
            });
            onClose();
        } finally {
            setLoading(false);
        }
    }, [postId, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        void loadContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, postId]);

    const handleSave = async (): Promise<void> => {
        if (!context) return;
        setSaving(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.post<SlideshowRerenderResponse>(
                `${backendUrl}/listing/slideshow/post/${postId}/re-render`,
                { texts, aiTexts, captionRenderId },
                { headers }
            );

            onRerenderStarted(data.jobId);
            onClose();
            toast.success(
                <CustomToast
                    message="Your video is being re-rendered with the new captions."
                    type="success"
                />,
                { autoClose: 4000 }
            );
        } catch (error) {
            // Leave the dialog open with the user's edits intact so nothing they typed
            // is lost — every rejection from this endpoint is human-readable.
            const message =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : "Couldn't save the captions. Please try again.";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 5000,
            });
        } finally {
            setSaving(false);
        }
    };

    const isBusy = loading || saving || isGeneratingText;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="relative flex h-[calc(90dvh-2rem)] flex-col overflow-y-hidden p-0 bg-white min-[1200px]:max-w-6xl sxl:max-w-7xl">
                <div className="relative shrink-0 border-b border-neutral-200 bg-white px-4 py-3 min-[640px]:px-6 min-[768px]:px-8">
                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary hover:text-primary focus:outline-none"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes className="h-6 w-6" />
                    </button>
                    <DialogHeader className="px-8">
                        <DialogTitle className="text-lg min-[640px]:text-xl min-[768px]:text-2xl">
                            Edit Video Captions
                        </DialogTitle>
                        <DialogDescription className="text-sm min-[640px]:text-base min-[768px]:text-lg">
                            Change the text shown over your slides, then save to
                            re-render the video
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="flex min-h-0 flex-1 flex-col space-y-6 overflow-y-auto px-4 pb-4 pt-4 min-[640px]:px-6 min-[640px]:pb-6 min-[768px]:px-8 min-[768px]:pb-8">
                    {loading ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-4">
                            <Spinner />
                            <p className="text-sm text-gray-500">
                                Loading the current captions…
                            </p>
                        </div>
                    ) : (
                        context && (
                            <SlideshowTextEditor
                                slides={slides}
                                texts={texts}
                                setTexts={setTexts}
                                setAiTexts={setAiTexts}
                                setCaptionRenderId={setCaptionRenderId}
                                listingId={context.listingId}
                                listingType={context.listingType as ListingType}
                                template={context.template as SlideshowTemplateId}
                                isGeneratingText={isGeneratingText}
                                setIsGeneratingText={setIsGeneratingText}
                            />
                        )
                    )}
                </div>

                <div className="flex shrink-0 justify-end border-t border-neutral-200 bg-white px-4 py-4 min-[640px]:px-6 min-[768px]:px-8">
                    <Button
                        id="save-slideshow-captions-btn"
                        onClick={handleSave}
                        disabled={isBusy || !context || slides.length === 0}
                    >
                        {saving ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving…
                            </>
                        ) : (
                            "Save & Re-render Video"
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditSlideshowCaptionsDialog;
